import { AsyncActionMetaOptions } from 'store/types';

import {
    DeleteRecordingActionPayload,
    DeleteSeriesRecordingActionPayload,
    ProtectRecordingActionPayload,
    ScheduleRecordingActionPayload,
    ScheduleSeriesRecordingActionPayload,
    ScheduleSeriesRecordingOptions,
    UnprotectRecordingActionPayload,
} from './actions.types';
import {
    CANCEL_RECORDING,
    CANCEL_RECORDING_FAILURE,
    CANCEL_RECORDING_SUCCESS,
    CANCEL_SERIES_RECORDING,
    CANCEL_SERIES_RECORDING_FAILURE,
    CANCEL_SERIES_RECORDING_SUCCESS,
    DELETE_RECORDING,
    DELETE_RECORDING_FAILURE,
    DELETE_RECORDING_SUCCESS,
    DELETE_SERIES_RECORDING,
    DELETE_SERIES_RECORDING_FAILURE,
    DELETE_SERIES_RECORDING_SUCCESS,
    GET_QUOTA_USAGE,
    GET_QUOTA_USAGE_FAILURE,
    GET_QUOTA_USAGE_SUCCESS,
    GET_RECORDINGS_CACHE,
    GET_RECORDINGS_CACHE_FAILURE,
    GET_RECORDINGS_CACHE_REQUEST,
    GET_RECORDINGS_CACHE_SUCCESS,
    PROTECT_RECORDING,
    PROTECT_RECORDING_FAILURE,
    PROTECT_RECORDING_SUCCESS,
    SCHEDULE_RECORDING,
    SCHEDULE_RECORDING_FAILURE,
    SCHEDULE_RECORDING_SUCCESS,
    SCHEDULE_SERIES_RECORDING,
    SCHEDULE_SERIES_RECORDING_FAILURE,
    SCHEDULE_SERIES_RECORDING_SUCCESS,
    SET_DELETE_ACTION_PIN_VALIDATION_TIME,
    UNPROTECT_RECORDING,
    UNPROTECT_RECORDING_FAILURE,
    UNPROTECT_RECORDING_SUCCESS,
} from './constants';
import {
    CachedRecordingsResponse,
    QuotaUsage,
    RecordingError,
    SeriesRecordingResponse,
} from './reducer.types';

const defaultScheduleRecordingPayload: Partial<ScheduleRecordingActionPayload> =
    {
        isProtected: false,
    };
const defaultScheduleSeriesRecordingPayload: Partial<ScheduleSeriesRecordingActionPayload> =
    {
        seriesType: 'SERIES',
        recordingOptions: [ScheduleSeriesRecordingOptions.ALL_EPISODES],
        numberOfEpisodesToKeep: 0,
    };

const defaultPinType = {
    pinType: 'AccessPin',
};

// GET QUOTA USAGE
export const getQuotaUsage = (
    meta: AsyncActionMetaOptions<QuotaUsage, RecordingError> = {},
) => ({
    type: GET_QUOTA_USAGE,
    meta,
});
export function getQuotaUsageSuccess(payload: QuotaUsage) {
    return {
        type: GET_QUOTA_USAGE_SUCCESS,
        payload,
    };
}
export const getQuotaUsageFailure = (payload: RecordingError) => ({
    type: GET_QUOTA_USAGE_FAILURE,
    payload,
});

// SCHEDULE RECORDING
export const scheduleRecording = (
    payload: ScheduleRecordingActionPayload,
    meta: AsyncActionMetaOptions<void, RecordingError> = {},
): {
    type: string;
    payload: ScheduleRecordingActionPayload;
    meta: AsyncActionMetaOptions<void, RecordingError>;
} => ({
    type: SCHEDULE_RECORDING,
    payload: {
        ...defaultScheduleRecordingPayload,
        ...payload,
    },
    meta,
});
export const scheduleRecordingSuccess = () => ({
    type: SCHEDULE_RECORDING_SUCCESS,
});
export const scheduleRecordingFailure = (payload: RecordingError) => ({
    type: SCHEDULE_RECORDING_FAILURE,
    payload,
});

// SCHEDULE SERIES RECORDING
export const scheduleSeriesRecording = (
    payload: ScheduleSeriesRecordingActionPayload,
    meta: AsyncActionMetaOptions<void, RecordingError> = {},
): {
    type: string;
    payload: ScheduleSeriesRecordingActionPayload;
    meta: AsyncActionMetaOptions<void, RecordingError>;
} => ({
    type: SCHEDULE_SERIES_RECORDING,
    payload: {
        ...defaultScheduleSeriesRecordingPayload,
        ...payload,
    },
    meta,
});
export const scheduleSeriesRecordingSuccess = () => ({
    type: SCHEDULE_SERIES_RECORDING_SUCCESS,
});
export const scheduleSeriesRecordingFailure = (payload: RecordingError) => ({
    type: SCHEDULE_SERIES_RECORDING_FAILURE,
    payload,
});

// PROTECT RECORDING
export const protectRecording = (
    payload: ProtectRecordingActionPayload,
    meta: AsyncActionMetaOptions<void, RecordingError> = {},
) => ({
    type: PROTECT_RECORDING,
    payload,
    meta,
});
export const protectRecordingSuccess = () => ({
    type: PROTECT_RECORDING_SUCCESS,
});
export const protectRecordingFailure = (payload: RecordingError) => ({
    type: PROTECT_RECORDING_FAILURE,
    payload,
});

// UNPROTECT RECORDING
export const unprotectRecording = (
    payload: UnprotectRecordingActionPayload,
    meta: AsyncActionMetaOptions<void, RecordingError> = {},
): {
    type: string;
    payload: UnprotectRecordingActionPayload;
    meta: AsyncActionMetaOptions<void, RecordingError>;
} => ({
    type: UNPROTECT_RECORDING,
    payload: {
        ...defaultPinType,
        ...payload,
    },
    meta,
});
export const unprotectRecordingSuccess = () => ({
    type: UNPROTECT_RECORDING_SUCCESS,
});
export const unprotectRecordingFailure = (payload: RecordingError) => ({
    type: UNPROTECT_RECORDING_FAILURE,
    payload,
});

// DELETE RECORDING
export const deleteRecording = (
    payload: DeleteRecordingActionPayload,
    meta: AsyncActionMetaOptions<void, RecordingError> = {},
) => ({
    type: DELETE_RECORDING,
    payload: {
        ...defaultPinType,
        ...payload,
    },
    meta,
});
export const deleteRecordingSuccess = () => ({
    type: DELETE_RECORDING_SUCCESS,
});
export const deleteRecordingFailure = (payload: RecordingError) => ({
    type: DELETE_RECORDING_FAILURE,
    payload,
});

// CANCEL RECORDING
export const cancelRecording = (
    payload: DeleteRecordingActionPayload,
    meta: AsyncActionMetaOptions<void, RecordingError[]> = {},
) => ({
    type: CANCEL_RECORDING,
    payload: {
        ...defaultPinType,
        ...payload,
    },
    meta,
});
export const cancelRecordingSuccess = () => ({
    type: CANCEL_RECORDING_SUCCESS,
});
export const cancelRecordingFailure = (payload: RecordingError) => ({
    type: CANCEL_RECORDING_FAILURE,
    payload,
});

// DELETE SERIES RECORDING
export const deleteSeriesRecording = (
    payload: DeleteSeriesRecordingActionPayload,
    meta: AsyncActionMetaOptions<void, RecordingError> = {},
) => ({
    type: DELETE_SERIES_RECORDING,
    payload: {
        ...defaultPinType,
        ...payload,
    },
    meta,
});
export const deleteSeriesRecordingSuccess = () => ({
    type: DELETE_SERIES_RECORDING_SUCCESS,
});
export const deleteSeriesRecordingFailure = (payload: RecordingError) => ({
    type: DELETE_SERIES_RECORDING_FAILURE,
    payload,
});

// CANCEL SERIES RECORDING
export const cancelSeriesRecording = (
    payload: DeleteSeriesRecordingActionPayload,
    meta: AsyncActionMetaOptions<void, RecordingError> = {},
) => ({
    type: CANCEL_SERIES_RECORDING,
    payload: {
        ...defaultPinType,
        ...payload,
    },
    meta,
});
export const cancelSeriesRecordingSuccess = () => ({
    type: CANCEL_SERIES_RECORDING_SUCCESS,
});
export const cancelSeriesRecordingFailure = (payload: RecordingError) => ({
    type: CANCEL_SERIES_RECORDING_FAILURE,
    payload,
});

export function getRecordingsCache(
    meta: AsyncActionMetaOptions = { autoRetry: true },
) {
    return { type: GET_RECORDINGS_CACHE, meta };
}

export function getRecordingsCacheRequest() {
    return { type: GET_RECORDINGS_CACHE_REQUEST };
}

export function getRecordingsCacheSuceess(payload: {
    timestamp: number;
    recordings: CachedRecordingsResponse['recordingSet'];
    series: SeriesRecordingResponse['series'];
}) {
    return {
        type: GET_RECORDINGS_CACHE_SUCCESS,
        payload,
    };
}

export function getRecordingsCacheFailure(error: any) {
    return { type: GET_RECORDINGS_CACHE_FAILURE, payload: { error } };
}

export function setDeleteActionPinValidationTime(
    profileId: string,
    timestamp: number | undefined,
) {
    return {
        type: SET_DELETE_ACTION_PIN_VALIDATION_TIME,
        payload: {
            profileId,
            timestamp,
        },
    };
}
